// @figma file=pkCGby54zXzTsccXvHqzO6 node=15455:53947 name="Alert Dialog" set="my-taible-shadcn-studio" verified=2026-03-22

import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import * as React from 'react';
import { cn } from '../../lib/utils';

// ─── AlertDialog (root) ───────────────────────────────────────────────────────

/**
 * Destructive confirmation modal that interrupts flow and requires explicit
 * user action to confirm or cancel.
 *
 * Built on `@radix-ui/react-alert-dialog` with full ARIA compliance:
 * - Role: `alertdialog`
 * - Focus trap: focus is captured inside the dialog while open
 * - Keyboard: `Escape` closes and focuses the cancel trigger
 * - ARIA: `aria-labelledby` links the title; `aria-describedby` links the description
 *
 * @example
 * ```tsx
 * <AlertDialog>
 *   <AlertDialogTrigger asChild>
 *     <Button variant="destructive">Delete account</Button>
 *   </AlertDialogTrigger>
 *   <AlertDialogContent>
 *     <AlertDialogHeader>
 *       <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
 *       <AlertDialogDescription>
 *         This action cannot be undone.
 *       </AlertDialogDescription>
 *     </AlertDialogHeader>
 *     <AlertDialogFooter>
 *       <AlertDialogCancel>Cancel</AlertDialogCancel>
 *       <AlertDialogAction>Continue</AlertDialogAction>
 *     </AlertDialogFooter>
 *   </AlertDialogContent>
 * </AlertDialog>
 * ```
 */

export interface AlertDialogProps
  extends React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Root> {}

const AlertDialog = AlertDialogPrimitive.Root;
AlertDialog.displayName = 'AlertDialog';

// ─── AlertDialogTrigger ───────────────────────────────────────────────────────

export interface AlertDialogTriggerProps
  extends React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Trigger> {}

const AlertDialogTrigger = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Trigger>,
  AlertDialogTriggerProps
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Trigger ref={ref} className={cn(className)} {...props} />
));
AlertDialogTrigger.displayName = 'AlertDialogTrigger';

// ─── AlertDialogPortal ────────────────────────────────────────────────────────

export interface AlertDialogPortalProps
  extends React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Portal> {}

const AlertDialogPortal = AlertDialogPrimitive.Portal;
AlertDialogPortal.displayName = 'AlertDialogPortal';

// ─── AlertDialogOverlay ───────────────────────────────────────────────────────

export interface AlertDialogOverlayProps
  extends React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay> {}

const AlertDialogOverlay = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Overlay>,
  AlertDialogOverlayProps
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    ref={ref}
    className={cn(
      // Backdrop fill — uses canvas token so it adapts to dark/light mode
      'fixed inset-0 z-50 bg-canvas/80 backdrop-blur-sm',
      // Entry/exit animations driven by Radix data-state
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
));
AlertDialogOverlay.displayName = 'AlertDialogOverlay';

// ─── AlertDialogContent ───────────────────────────────────────────────────────

export interface AlertDialogContentProps
  extends React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content> {}

const AlertDialogContent = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Content>,
  AlertDialogContentProps
>(({ className, children, ...props }, ref) => (
  <AlertDialogPortal>
    <AlertDialogOverlay />
    <AlertDialogPrimitive.Content
      ref={ref}
      className={cn(
        // Positioning
        'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
        // Layout
        'flex w-full max-w-lg flex-col gap-4',
        // Surface
        'bg-background rounded-[var(--radius)] border border-border',
        // Spacing — matches Figma 24px padding
        'p-6',
        // Elevation — matches Figma Light/box-shadow/default/lg
        'shadow-lg',
        // Focus ring
        'focus:outline-none',
        // Entry/exit animations
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
        'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
        'duration-200',
        className,
      )}
      {...props}
    >
      {children}
    </AlertDialogPrimitive.Content>
  </AlertDialogPortal>
));
AlertDialogContent.displayName = 'AlertDialogContent';

// ─── AlertDialogHeader ────────────────────────────────────────────────────────

export interface AlertDialogHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}

const AlertDialogHeader = React.forwardRef<HTMLDivElement, AlertDialogHeaderProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col gap-2 text-left', className)} {...props} />
  ),
);
AlertDialogHeader.displayName = 'AlertDialogHeader';

// ─── AlertDialogFooter ────────────────────────────────────────────────────────

export interface AlertDialogFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

const AlertDialogFooter = React.forwardRef<HTMLDivElement, AlertDialogFooterProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        // Row layout, right-aligned — matches Figma layout_F5WG3R / layout_9IUH56
        'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
        className,
      )}
      {...props}
    />
  ),
);
AlertDialogFooter.displayName = 'AlertDialogFooter';

// ─── AlertDialogTitle ─────────────────────────────────────────────────────────

export interface AlertDialogTitleProps
  extends React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title> {}

const AlertDialogTitle = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Title>,
  AlertDialogTitleProps
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Title
    ref={ref}
    className={cn(
      // Figma: Tailwind/LG/Semi Bold — text-lg font-semibold
      'text-lg font-semibold leading-snug text-foreground',
      className,
    )}
    {...props}
  />
));
AlertDialogTitle.displayName = 'AlertDialogTitle';

// ─── AlertDialogDescription ───────────────────────────────────────────────────

export interface AlertDialogDescriptionProps
  extends React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description> {}

const AlertDialogDescription = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Description>,
  AlertDialogDescriptionProps
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    className={cn(
      // Figma: Tailwind/SM/Regular — text-sm text-muted-foreground
      'text-sm text-muted-foreground',
      className,
    )}
    {...props}
  />
));
AlertDialogDescription.displayName = 'AlertDialogDescription';

// ─── AlertDialogAction ────────────────────────────────────────────────────────

/**
 * The confirm/proceed button inside AlertDialogFooter.
 * Defaults to a destructive style — applies `bg-destructive` token.
 * Override with `className` to apply a different visual treatment.
 */
export interface AlertDialogActionProps
  extends React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action> {}

const AlertDialogAction = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Action>,
  AlertDialogActionProps
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Action
    ref={ref}
    className={cn(
      // Base button shape
      'inline-flex h-9 items-center justify-center rounded-[var(--radius)] px-4 text-sm font-medium',
      // Transition
      'transition-colors',
      // Destructive fill — matches semantic purpose of this component
      'bg-destructive text-destructive-foreground',
      'hover:bg-[var(--color-destructive-hover,var(--color-destructive))] focus-visible:bg-[var(--color-destructive-hover,var(--color-destructive))]',
      // Focus ring
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      // Disabled
      'disabled:pointer-events-none disabled:opacity-50',
      className,
    )}
    {...props}
  />
));
AlertDialogAction.displayName = 'AlertDialogAction';

// ─── AlertDialogCancel ────────────────────────────────────────────────────────

/**
 * The cancel button inside AlertDialogFooter.
 * Defaults to an outline style — applies `border-border bg-background` tokens.
 */
export interface AlertDialogCancelProps
  extends React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel> {}

const AlertDialogCancel = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Cancel>,
  AlertDialogCancelProps
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Cancel
    ref={ref}
    className={cn(
      // Base button shape
      'inline-flex h-9 items-center justify-center rounded-[var(--radius)] px-4 text-sm font-medium',
      // Transition
      'transition-colors',
      // Outline fill — matches Figma Outline Button style
      'border border-border bg-background text-foreground',
      'hover:bg-paper focus-visible:bg-paper',
      // Focus ring
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      // Disabled
      'disabled:pointer-events-none disabled:opacity-50',
      // On mobile, cancel sits at bottom — override mt to match sm:row layout
      'mt-2 sm:mt-0',
      className,
    )}
    {...props}
  />
));
AlertDialogCancel.displayName = 'AlertDialogCancel';

// ─── Exports ──────────────────────────────────────────────────────────────────

export {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
};
